// Open a level on demand.
//
// A run always opened on the opening scenario and nothing else: `App.tsx`
// named `openingScenario()` and the only way to see any other map was to win
// through to it. So the question "what does this level look like" could
// only be asked of one level, and a map still being drawn in Tiled could only
// be looked at through `npm run level`, which draws nothing.
//
// Two doors: a shipped level by id, or a `.tmj` off disk. Both go through the
// loader the run itself uses (`loadLevelSource`), so what opens here is what
// a player would get — same parser, same validation, same faults.
//
// **Not a command.** Opening a level is `new-game`, which `App.tsx` already
// sends from the loader effect; this hands that effect a source and lets it do
// what Restart does. A second `new-game` site here would be a second copy of
// the seed, ruleset and mode logic to drift.

import { useRef, useState, type ChangeEvent } from "react";

import { type LevelSource, openingScenario, SCENARIOS } from "src/engine/campaign/levels";
import { Btn, HoverCard, Select } from "../kit";
import { readLevelFile } from "./levelFile";
import { useBesidePanel, Fold, Note, Row } from "./parts";

export function LoadLevel({
  inRun,
  onLoad
}: {
  /** A level is a run's board, so there has to be a run to put one in. */
  inRun: boolean;
  /** Open this source in place of the board. Absent outside a run. */
  onLoad?: (source: LevelSource) => void;
}) {
  const [picked, setPicked] = useState<string>(openingScenario().id);
  const [problem, setProblem] = useState<string>();
  const file = useRef<HTMLInputElement | null>(null);
  const panel = useBesidePanel();
  const scenario = SCENARIOS.find((entry) => entry.id === picked);

  const fromFile = async (event: ChangeEvent<HTMLInputElement>) => {
    const chosen = event.target.files?.[0];
    // Cleared, or picking the same file again after fixing it fires nothing.
    event.target.value = "";
    if (!chosen || !onLoad) return;
    try {
      onLoad(readLevelFile(chosen.name, await chosen.text()));
      setProblem(undefined);
    } catch (error) {
      setProblem(error instanceof Error ? error.message : String(error));
    }
  };

  return (
    <Fold id="level" title="Load a level">
      {!inRun || !onLoad ? (
        // Said out loud rather than by disabling the buttons silently — the
        // same reason `GrantCards` says it.
        <Note>
          A level is a run&apos;s board, and this game has no run. Start one from the menu.
        </Note>
      ) : (
        <>
          <Select
            data-testid="dev-level-pick"
            value={picked}
            onChange={(event) => setPicked(event.target.value)}
          >
            {SCENARIOS.map((entry) => (
              <option key={entry.id} value={entry.id}>
                {entry.name}
              </option>
            ))}
          </Select>
          {scenario && <Note>{scenario.blurb}</Note>}
          <Row>
            <HoverCard
              placement="right"
              beside={panel}
              title="Open this level"
              card="Throws the board away and opens the level fresh, seed and difficulty kept. Restart then restarts this level."
            >
              <Btn
                small
                variant="primary"
                data-testid="dev-level-load"
                onClick={() => onLoad(picked)}
              >
                Open
              </Btn>
            </HoverCard>
            <HoverCard
              placement="right"
              beside={panel}
              title="Open a .tmj off disk"
              card="A map still being drawn in Tiled, read by the same parser. Its tilesets resolve against the shipped ones under public/levels/."
            >
              <Btn small data-testid="dev-level-browse" onClick={() => file.current?.click()}>
                From a file…
              </Btn>
            </HoverCard>
            {/* Hidden rather than styled: a native file control cannot wear
                the kit's face, and a `label` around one is a second button
                shape. `setInputFiles` reaches a hidden input fine. */}
            <input
              ref={file}
              type="file"
              accept=".tmj,application/json"
              hidden
              data-testid="dev-level-file"
              onChange={(event) => void fromFile(event)}
            />
          </Row>
          {problem && <Note data-testid="dev-level-problem">{problem}</Note>}
        </>
      )}
    </Fold>
  );
}
